import React, { useEffect, useState } from 'react';
import { Box, Stack, Tab, Tabs } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import useSalesInvoiceRegisterListFilters from '../hooks/useSalesInvoiceRegisterListFilters';
import SalesInvoiceRegisterListFilters from '../components/SalesInvoiceRegisterListFilters';
import TopBar from '../../../components/TopBar';
import FeatureNotSupportedDialog from '../../common/FeatureNotSupportedDialog';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import SalesInvoiceRegisterTable from '../components/SalesInvoiceRegisterTable';
import SalesInvoiceRegisterListActions from '../components/SalesInvoiceRegisterListActions';
import SalesCreditNoteRegisterTable from '../components/SalesCreditNoteRegisterTable';
import SalesCreditNoteRegisterListActions from '../components/SalesCreditNoteRegisterListActions';

const TAB_KEYS = ['invoices', 'credit-notes'];

function SalesInvoiceRegisterListPage() {
  const { salesInvoiceRegister, eInvoiceForm, eInvoiceListPage } = useTranslatedMessages();
  useDocumentTitle(salesInvoiceRegister?.pages?.document_title ?? 'Sales Invoice Register');
  const navigate = useNavigate();
  const location = useLocation();
  const subscription = useSelector((state) => state.subscription.subscription);
  const isRegisterFeatureEnabled = subscription?.features?.enable_register;

  const tabs = [
    { key: 'invoices', label: eInvoiceForm.document_list.tab_invoices },
    { key: 'credit-notes', label: eInvoiceForm.document_list.tab_credit_notes },
  ];

  const queryParams = new URLSearchParams(location.search);
  const tabFromUrl = queryParams.get('tab');
  const initialTab = TAB_KEYS.includes(tabFromUrl) ? tabFromUrl : 'invoices';
  const [selectedTab, setSelectedTab] = useState(initialTab);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    navigate(`/sales-invoice-register?tab=${newValue}`);
  };

  const getTopBarTitle = (tab) => {
    const pages = salesInvoiceRegister?.pages ?? {};
    switch (tab) {
      case 'invoices':
        return pages.list_title_invoices ?? pages.list_title ?? 'Sales Invoice Register';
      case 'credit-notes':
        return pages.list_title_credit_notes ?? 'Sales Credit Note Register';
      default:
        return pages.list_title ?? 'Sales Invoice Register';
    }
  };

  useEffect(() => {
    setSelectedTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (tabFromUrl && !TAB_KEYS.includes(tabFromUrl)) {
      navigate('/sales-invoice-register?tab=invoices', { replace: true });
    }
  }, [tabFromUrl, navigate]);

  const filters = [
    {
      type: 'search',
      options: [
        {
          key: 'document_number',
          placeholder:
            selectedTab === 'credit-notes'
              ? (eInvoiceListPage?.filters?.credit_note_number_label ?? 'Credit Note Number')
              : (eInvoiceListPage?.filters?.invoice_number_label ?? 'Invoice Number'),
        },
        { key: 'billed_to_name', placeholder: eInvoiceListPage?.filters?.billed_to_name ?? 'Buyer Name' },
        { key: 'billed_to_vat', placeholder: eInvoiceListPage?.filters?.billed_to_vat ?? 'Buyer VAT' },
        { key: 'billed_by_name', placeholder: eInvoiceListPage?.filters?.billed_by_name ?? 'Seller Name' },
        { key: 'billed_by_vat', placeholder: eInvoiceListPage?.filters?.billed_by_vat ?? 'Seller VAT' },
      ],
    },
  ];

  const {
    selectedFilter,
    searchTerm,
    currentQuery,
    dateRange,
    documentModeFilter,
    handleFilterChange,
    handleSearchChange,
    handleSearchKeyPress,
    handleDateRangeChange,
    handleDocumentModeFilterChange,
    handlePaginationChange,
  } = useSalesInvoiceRegisterListFilters({
    searchOnChange: true,
    persistKey: `sales-invoice-register/list:${selectedTab}`,
  });

  if (!isRegisterFeatureEnabled) {
    return (
      <FeatureNotSupportedDialog
        title={salesInvoiceRegister?.feature_not_supported?.title ?? 'Sales Invoice Register'}
        description={
          salesInvoiceRegister?.feature_not_supported?.description ??
          'Register allows you to track transactions that are currently not captured as e-invoices.'
        }
      />
    );
  }

  return (
    <Stack direction="column" spacing={3} sx={{ mt: '-1rem' }}>
      <TopBar title={getTopBarTitle(selectedTab)} />
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Tabs value={selectedTab} onChange={handleTabChange}>
          {tabs.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>
        {selectedTab === 'invoices' && <SalesInvoiceRegisterListActions />}
        {selectedTab === 'credit-notes' && <SalesCreditNoteRegisterListActions />}
      </Box>

      {selectedTab === 'invoices' ? (
        <>
          <Box mb={2}>
            <SalesInvoiceRegisterListFilters
              filters={filters}
              selectedFilter={selectedFilter}
              onFilterChange={handleFilterChange}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              onSearchKeyPress={handleSearchKeyPress}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              documentModeFilter={documentModeFilter}
              onDocumentModeFilterChange={handleDocumentModeFilterChange}
            />
          </Box>
          <SalesInvoiceRegisterTable handlePaginationChange={handlePaginationChange} searchQuery={currentQuery} />
        </>
      ) : (
        <>
          <Box mb={2}>
            <SalesInvoiceRegisterListFilters
              filters={filters}
              selectedFilter={selectedFilter}
              onFilterChange={handleFilterChange}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              onSearchKeyPress={handleSearchKeyPress}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              documentModeFilter={documentModeFilter}
              onDocumentModeFilterChange={handleDocumentModeFilterChange}
            />
          </Box>
          <SalesCreditNoteRegisterTable handlePaginationChange={handlePaginationChange} searchQuery={currentQuery} />
        </>
      )}
    </Stack>
  );
}

export default SalesInvoiceRegisterListPage;
